import { TOOL_TYPE_TO_DISPLAY_MAPPING } from "../../constants";
import { CrmImportJob } from "../../hooks/types";
import { ChipVariant } from "./components/Chip";
import { ProjectSetupData, FileData, FileSummary } from "./types";

/**
 * Transform CRM import job data to ProjectSetupData format
 */
export const transformJobToProjectSetup = (job: CrmImportJob): ProjectSetupData => {
  return {
    arenaDomainName: job.arenaDomain || "Unknown Arena",
    arenaUserEmail: job.arenaUserEmail || "Unknown Email",
    jobName: job.jobName || `${job.arenaDomain} CRM Import` || "Unknown Job",
  };
};

/**
 * Transform job config tools to FileData array
 */
export const transformJobToFiles = (job: CrmImportJob): FileData[] => {
  if (!job.parsedJobConfig?.tools) {
    return [];
  }

  return job.parsedJobConfig.tools
    .map((tool, index) => {
      const entityType = TOOL_TYPE_TO_DISPLAY_MAPPING[tool.tool_type] || tool.tool_type;
      const fileName = getFileNameFromTool(tool);

      if (!fileName) {
        return null;
      }

      return {
        id: `${tool.tool_id || index}`,
        fileName,
        fileSize: getFileSizeFromTool(tool),
        entityType,
      };
    })
    .filter((file): file is FileData => file !== null);
};

/**
 * Calculate file summary from files array
 */
export const calculateFileSummary = (files: FileData[]): FileSummary => {
  const totalFiles = files.length;
  const uniqueEntityTypes = new Set(files.map((file) => file.entityType));

  const totalSizeKB = files.reduce((total, file) => {
    // Extract KB value from fileSize string (format: "X.X KB • CSV File")
    const match = file.fileSize.match(/^(\d+\.?\d*)\s*KB/);
    const sizeKB = match ? parseFloat(match[1]) : 0;
    return total + sizeKB;
  }, 0);

  const totalSize = totalSizeKB.toFixed(1);

  return {
    totalFiles,
    totalSize,
    dataTypes: uniqueEntityTypes.size,
  };
};

/**
 * Extract file name from tool configuration
 */
const getFileNameFromTool = (tool: any): string | null => {
  const fileKeys = ["position_file", "contact_file", "note_file", "comm_settings_file"];
  for (const fileKey of fileKeys) {
    const fileOption = tool.options?.find((opt: any) => opt.key === fileKey);
    if (fileOption?.value) {
      // Extract just the filename from the full path (e.g., "gemini/accounts_base (6).csv" -> "accounts_base (6).csv")
      const fullPath = fileOption.value;
      const fileName = fullPath.includes("/") ? fullPath.split("/").pop() : fullPath;
      return fileName || fullPath;
    }
  }

  return null;
};

/**
 * Extract file size from tool configuration
 */
const getFileSizeFromTool = (tool: any): string => {
  const sizeOption = tool.options?.find((opt: any) => opt.key === "fileSize");
  if (sizeOption?.value) {
    return sizeOption.value;
  }

  // Fallback to 0.0 KB instead of "Unknown"
  return "0.0 KB • CSV File";
};

/**
 * Check if job data is valid for display
 */
export const isValidJobData = (job: CrmImportJob | undefined): boolean => {
  return !!(job?.jobId && job?.arenaDomain && job?.arenaUserEmail);
};

/**
 * Transform job config for submission to the API
 * Converts the parsed job config format to the expected API format
 */
export const transformJobConfigForSubmission = (job: CrmImportJob) => {
  return {
    tools:
      job.parsedJobConfig?.tools?.map((tool) => ({
        toolId: tool.tool_id,
        toolType: tool.tool_type,
        options: tool.options || [],
      })) || [],
  };
};

export type ComponentState =
  | { type: "loading" }
  | {
      type: "error";
      title: string;
      message: string;
      showRetry: boolean;
    }
  | { type: "success" };

/**
 * Determines the current component state based on loading, error, and data conditions
 * Used for consistent error handling across components
 */
export const getComponentState = (
  idFromUrl: string | null,
  jobLoading: boolean,
  jobError: any,
  job: any,
  isValidJobDataFn: (job: any) => boolean,
): ComponentState => {
  if (!idFromUrl) {
    return {
      type: "error",
      title: "Missing Job ID",
      message: "No job ID found in URL. Please navigate from a valid job link.",
      showRetry: false,
    };
  }

  if (jobLoading) {
    return { type: "loading" };
  }

  if (jobError) {
    return {
      type: "error",
      title: "Failed to Load Job",
      message: jobError.message,
      showRetry: true,
    };
  }

  if (!job || !isValidJobDataFn(job)) {
    return {
      type: "error",
      title: "Job Not Found",
      message: "The requested job could not be found or contains invalid data.",
      showRetry: true,
    };
  }

  return { type: "success" };
};

/**
 * Maps entity types to chip variants for consistent styling
 * Used by DataFilesEntityChoices component and potentially other components
 */
export const getChipVariant = (entityType: string): ChipVariant => {
  switch (entityType) {
    case "Contacts":
      return "contacts";
    case "Accounts":
      return "accounts";
    case "Opportunities":
      return "opportunities";
    case "Activities":
      return "activities";
    default:
      return "contacts";
  }
};
